import React from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from "@mui/material";
import LicenceTable from "../LicenceTable";
import { useTranslation } from "react-i18next";

const formatEuro = (val) => {
  if (val === null || val === undefined) return "—";
  const num = Number(val);
  if (Number.isNaN(num)) return "—";
  return `${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
};

// Helper function to get logged-in partner ID from localStorage
const getLoggedPartnerId = () => {
  try {
    const userData = localStorage.getItem("user");
    if (!userData) return null;

    const user = JSON.parse(userData);
    const customerId = user?.customerId;
    if (!customerId) return null;

    // Normalize to number if possible
    return isNaN(Number(customerId)) ? customerId : Number(customerId);
  } catch (e) {
    return null;
  }
};

// Helper function to get VOC price for a product, partner, and market
const getProductVocPrice = (product, partnerId, market = "sk") => {
  if (!partnerId || !product?.partners) return null;

  // Find partner entry by matching ID (loose numeric comparison)
  for (const [key, partnerData] of Object.entries(product.partners)) {
    if (Number(partnerData.id) === Number(partnerId)) {
      // If partner price is a number (backward compatibility), return it
      if (typeof partnerData.price === "number") {
        return partnerData.price;
      }

      // If partner price is an object, return market-specific price
      if (typeof partnerData.price === "object" && partnerData.price[market]) {
        return partnerData.price[market];
      }

      // Fallback to 'sk' if market not found
      return partnerData.price.sk || null;
    }
  }

  return null;
};

// Helper function to get MOC price for a product and market
const getProductMocPrice = (product, market = "sk") => {
  if (!product?.price) return 0;

  // If price is a number (backward compatibility), return it
  if (typeof product.price === "number") {
    return product.price;
  }

  // If price is an object, return market-specific price
  if (typeof product.price === "object" && product.price[market]) {
    return product.price[market];
  }

  // Fallback to 'sk' if market not found
  return product.price.sk || 0;
};

const commitmentText = (commitmentKey, options) => {
  if (commitmentKey === "monthly") return "Mesačne";
  const opt = options?.[commitmentKey];
  const months = Number(opt?.months || 0);
  if (!months) return commitmentKey;
  return `${months} mesiacov`;
};

const CustomerQuote = ({ data, config }) => {
  const { i18n, t } = useTranslation();
  if (!data) return null;
  const {
    snapshot,
    totalsCustomerOnly,
    totalsPartnerOnly,
    installationTotals,
    kiosksMocTotal = 0,
    kiosksVocTotal = 0,
    grandCustomerOnly,
    formatPrice: formatPriceProp,
  } = data;
  const fmt = formatPriceProp || formatEuro;
  const {
    licenseType,
    kioskCount,
    commitmentOptionKey,
    products = {},
    productsMarket = "sk",
  } = snapshot || {};
  const licenseName = config?.licenses?.[licenseType]?.name || licenseType;
  const loggedPartnerId = getLoggedPartnerId();
  const months = Number(
    config?.commitmentDiscounts?.options?.[commitmentOptionKey]?.months || 1
  );
  const finalMonthly =
    totalsCustomerOnly?.customerMonthlyFinal ??
    totalsCustomerOnly?.customerMonthlyBase ??
    null;
  const perKioskFinal =
    finalMonthly !== null && kioskCount ? finalMonthly / kioskCount : null;
  const install = snapshot?.installation || {};
  // Inputs preserved for potential future display; not needed for current quote layout
  // const travelKm = Number(install?.travelKm || install?.km || 0);
  // const installHours = Number(
  //   install?.installationHours ?? install?.installHours ?? 0
  // );
  // const systemSetupCount = Number(install?.systemSetupCount || kioskCount || 0);
  const selectedPlace = install?.place ||
    install?.address || { formattedAddress: "" };
  const hasPlaceSelected =
    Boolean((selectedPlace?.formattedAddress || "").trim()) ||
    Boolean(selectedPlace?.placeId);

  const resolveLangKey = (lang) => {
    if (!lang) return "en";
    if (lang === "cs") return "cz";
    return lang;
  };
  const currentLang = resolveLangKey(i18n?.language);
  const getFeeLabel = (labels) => {
    if (labels && labels[currentLang]) return labels[currentLang];
    return labels?.en || labels?.sk || labels?.cz || labels?.de || "";
  };

  // Compute selected products for KIOSKY section
  const selectedProducts =
    config?.products?.filter((product) => (products[product.code] || 0) > 0) ||
    [];
  const hasAnyVoc =
    loggedPartnerId &&
    selectedProducts.some(
      (product) =>
        getProductVocPrice(product, loggedPartnerId, productsMarket) !== null
    );

  // Check if license has partner pricing
  const hasLicenseVoc =
    loggedPartnerId && totalsPartnerOnly?.partnerMonthlyBase !== null;

  return (
    <Paper variant="outlined" sx={{ mt: 2 }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
          Cenová ponuka
        </Typography>
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <colgroup>
              <col style={{ width: hasLicenseVoc ? "40%" : "50%" }} />
              <col style={{ width: hasLicenseVoc ? "20%" : "25%" }} />
              <col style={{ width: hasLicenseVoc ? "20%" : "25%" }} />
              {hasLicenseVoc ? <col style={{ width: "20%" }} /> : null}
            </colgroup>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Položka</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Pôvodná cena
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Cena po zľave
                </TableCell>
                {hasLicenseVoc && (
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    VOC
                  </TableCell>
                )}
              </TableRow>
              <TableRow>
                <TableCell>{`Licencia: ${licenseName} (mesačne)`}</TableCell>
                <TableCell align="right">
                  {fmt(totalsCustomerOnly?.customerMonthlyBase)}
                </TableCell>
                <TableCell align="right">
                  <b>{fmt(finalMonthly)}</b>
                </TableCell>
                {hasLicenseVoc && (
                  <TableCell align="right">
                    <b>{fmt(totalsPartnerOnly?.partnerMonthlyFinal)}</b>
                  </TableCell>
                )}
              </TableRow>
              {Number(kioskCount) > 1 ? (
                <>
                  <TableRow>
                    <TableCell>Cena za 1 kiosk (mesačne)</TableCell>
                    <TableCell align="right">
                      {fmt(
                        (totalsCustomerOnly?.customerMonthlyBase ?? 0) /
                          Number(kioskCount || 1)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {fmt(perKioskFinal)}
                    </TableCell>
                    {hasLicenseVoc && (
                      <TableCell align="right">
                        {fmt(
                          (totalsPartnerOnly?.partnerMonthlyFinal ?? 0) /
                            Number(kioskCount || 1)
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                  <TableRow>
                    <TableCell>{`Počet kioskov: ${kioskCount}`}</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">—</TableCell>
                    {hasLicenseVoc && <TableCell align="right">—</TableCell>}
                  </TableRow>
                </>
              ) : null}
              <TableRow>
                <TableCell>Spolu (mesačne)</TableCell>
                <TableCell align="right">
                  {fmt(totalsCustomerOnly?.customerMonthlyBase)}
                </TableCell>
                <TableCell align="right">
                  <b>{fmt(finalMonthly)}</b>
                </TableCell>
                {hasLicenseVoc && (
                  <TableCell align="right">
                    <b>{fmt(totalsPartnerOnly?.partnerMonthlyBase)}</b>
                  </TableCell>
                )}
              </TableRow>
              {/* <TableRow>
                <TableCell>
                  Viazanosť:{"*** "}
                  {commitmentText(
                    commitmentOptionKey,
                    config?.commitmentDiscounts?.options
                  )}
                </TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">—</TableCell>
              </TableRow> */}
              <TableRow>
                <TableCell>
                  Platba:{" "}
                  {commitmentText(
                    commitmentOptionKey,
                    config?.commitmentDiscounts?.options
                  )}
                </TableCell>
              </TableRow>
              {Number(totalsCustomerOnly?.customerKioskDiscountAmountMonthly) >
              0 ? (
                <TableRow>
                  <TableCell>Zľava za počet kioskov</TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right">
                    <b>
                      -
                      {fmt(
                        totalsCustomerOnly?.customerKioskDiscountAmountMonthly
                      )}
                    </b>
                  </TableCell>
                  {hasLicenseVoc && (
                    <TableCell align="right">
                      <b>
                        -
                        {fmt(
                          totalsPartnerOnly?.partnerKioskDiscountAmountMonthly
                        )}
                      </b>
                    </TableCell>
                  )}
                </TableRow>
              ) : null}
              {Number(
                totalsCustomerOnly?.customerCommitmentDiscountAmountMonthly
              ) > 0 ? (
                <TableRow>
                  <TableCell>Zľava za viazanosť</TableCell>
                  <TableCell align="right"></TableCell>
                  <TableCell align="right">
                    <b>
                      -
                      {fmt(
                        totalsCustomerOnly?.customerCommitmentDiscountAmountMonthly
                      )}
                    </b>
                  </TableCell>
                  {hasLicenseVoc && (
                    <TableCell align="right">
                      <b>
                        -
                        {fmt(
                          totalsPartnerOnly?.partnerCommitmentDiscountAmountMonthly
                        )}
                      </b>
                    </TableCell>
                  )}
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="h6" sx={{ mb: 1 }}>
          Inštalácia (jednorazovo)
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          Adresa inštalácie:{" "}
          <b>{(selectedPlace?.formattedAddress || "").trim() || "—"}</b>
        </Typography>
        {!hasPlaceSelected ? (
          <Typography
            variant="caption"
            sx={{ color: "error.main", display: "block", mb: 1 }}
          >
            Adresa inštalácie nebola vybraná z Google Places. Vráťte sa do
            kalkulácie a vyberte adresu.
          </Typography>
        ) : null}
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <colgroup>
              <col style={{ width: "70%" }} />
              <col style={{ width: "30%" }} />
            </colgroup>
            <TableBody>
              <TableRow>
                <TableCell>
                  {getFeeLabel(config?.installationFees?.travelCosts?.label)}
                </TableCell>
                <TableCell align="right">
                  {fmt(installationTotals?.travelCost)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {getFeeLabel(
                    config?.installationFees?.installationWork?.label
                  )}
                </TableCell>
                <TableCell align="right">
                  {fmt(installationTotals?.installWorkCost)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  {getFeeLabel(config?.installationFees?.systemSetup?.label)}
                </TableCell>
                <TableCell align="right">
                  {installationTotals?.systemSetupCost !== null
                    ? fmt(installationTotals?.systemSetupCost)
                    : "—"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <b>Spolu inštalácia</b>
                </TableCell>
                <TableCell align="right">
                  <b>{fmt(installationTotals?.installationTotal)}</b>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* KIOSKY section */}
        {selectedProducts.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              KIOSKY
            </Typography>
            <TableContainer sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Zariadenie</TableCell>
                    {hasAnyVoc && (
                      <>
                        <TableCell align="right">MOC / ks</TableCell>
                        <TableCell align="right">VOC / ks</TableCell>
                      </>
                    )}
                    {!hasAnyVoc && (
                      <TableCell align="right">Cena / ks</TableCell>
                    )}
                    <TableCell align="right">Počet ks</TableCell>
                    {hasAnyVoc && (
                      <>
                        <TableCell align="right">MOC spolu</TableCell>
                        <TableCell align="right">VOC spolu</TableCell>
                      </>
                    )}
                    {!hasAnyVoc && <TableCell align="right">Spolu</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedProducts.map((product) => {
                    const quantity = products[product.code] || 0;
                    const mocPrice = getProductMocPrice(
                      product,
                      productsMarket
                    );
                    const mocItemTotal = mocPrice * quantity;

                    const vocPrice = loggedPartnerId
                      ? getProductVocPrice(
                          product,
                          loggedPartnerId,
                          productsMarket
                        )
                      : null;
                    const vocItemTotal =
                      vocPrice !== null ? vocPrice * quantity : null;

                    return (
                      <TableRow key={product.code}>
                        <TableCell>{product.name}</TableCell>
                        {hasAnyVoc && (
                          <>
                            <TableCell align="right">
                              {fmt(mocPrice)}
                            </TableCell>
                            <TableCell align="right">
                              {vocPrice !== null ? fmt(vocPrice) : "—"}
                            </TableCell>
                          </>
                        )}
                        {!hasAnyVoc && (
                          <TableCell align="right">
                            {fmt(mocPrice)}
                          </TableCell>
                        )}
                        <TableCell align="right">{quantity}</TableCell>
                        {hasAnyVoc && (
                          <>
                            <TableCell align="right">
                              {fmt(mocItemTotal)}
                            </TableCell>
                            <TableCell align="right">
                              {vocItemTotal !== null
                                ? fmt(vocItemTotal)
                                : "—"}
                            </TableCell>
                          </>
                        )}
                        {!hasAnyVoc && (
                          <TableCell align="right">
                            {fmt(mocItemTotal)}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Spolu</TableCell>
                    {hasAnyVoc && (
                      <>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          —
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          —
                        </TableCell>
                      </>
                    )}
                    {!hasAnyVoc && (
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        —
                      </TableCell>
                    )}
                    <TableCell align="right" sx={{ fontWeight: "bold" }}>
                      —
                    </TableCell>
                    {hasAnyVoc && (
                      <>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {fmt(kiosksMocTotal)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          {fmt(kiosksVocTotal)}
                        </TableCell>
                      </>
                    )}
                    {!hasAnyVoc && (
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {fmt(kiosksMocTotal)}
                      </TableCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <colgroup>
              <col style={{ width: "50%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "25%" }} />
            </colgroup>
            <TableBody>
              {months > 1 ? (
                <>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      Suma za obdobie
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Pôvodná suma
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Suma po zľave
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>{`Spolu za ${months} mesiacov (licencia)`}</TableCell>
                    <TableCell align="right">
                      {fmt(
                        (totalsCustomerOnly?.customerMonthlyBase || 0) * months
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <b>
                        {fmt(
                          (finalMonthly !== null
                            ? finalMonthly
                            : totalsCustomerOnly?.customerMonthlyBase || 0) *
                            months
                        )}
                      </b>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Zľava spolu za obdobie</TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">
                      <b>
                        -
                        {fmt(
                          Math.max(
                            0,
                            ((totalsCustomerOnly?.customerMonthlyBase || 0) -
                              (finalMonthly !== null
                                ? finalMonthly
                                : totalsCustomerOnly?.customerMonthlyBase ||
                                  0)) *
                              months
                          )
                        )}
                      </b>
                    </TableCell>
                  </TableRow>
                </>
              ) : null}
              <TableRow>
                <TableCell>Spolu prvý mesiac</TableCell>
                <TableCell align="right">—</TableCell>
                <TableCell align="right">
                  <b>{fmt(grandCustomerOnly?.customerFirstMonth)}</b>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Overall summary with monthly and one-time costs */}
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Položka</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  MOC
                </TableCell>
                {hasAnyVoc && (
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    VOC
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Licencia spolu (mesačne)</TableCell>
                <TableCell align="right">{fmt(finalMonthly)}</TableCell>
                {hasAnyVoc && <TableCell align="right">—</TableCell>}
              </TableRow>
              <TableRow>
                <TableCell>Inštalácia (jednorazovo)</TableCell>
                <TableCell align="right">
                  {fmt(installationTotals?.installationTotal)}
                </TableCell>
                {hasAnyVoc && <TableCell align="right">—</TableCell>}
              </TableRow>
              {selectedProducts.length > 0 && (
                <TableRow>
                  <TableCell>Kiosk</TableCell>
                  <TableCell align="right">
                    {fmt(kiosksMocTotal)}
                  </TableCell>
                  {hasAnyVoc && (
                    <TableCell align="right">
                      {fmt(kiosksVocTotal)}
                    </TableCell>
                  )}
                </TableRow>
              )}
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Spolu jednorazovo
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  {fmt(
                    (installationTotals?.installationTotal || 0) +
                      kiosksMocTotal
                  )}
                </TableCell>
                {hasAnyVoc && (
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {fmt(
                      (installationTotals?.installationTotal || 0) +
                        kiosksVocTotal
                    )}
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          Všetky ceny sú uvedené bez DPH 23%.
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
          Typy licencií
        </Typography>
        <LicenceTable forceHidePartner priceHeaderLabel="Cena" />
      </Box>
    </Paper>
  );
};

export default CustomerQuote;
